import React, { useState } from 'react';
import { Box, TextField, Tabs, Tab, Typography, Chip, Stack, Paper } from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';
import { SUPPORTED_LANGUAGES, LanguageTabConfig } from '../types';

interface MultiLanguageInputProps {
  label?: string;
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  maxRows?: number;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  requiredLanguages?: string[];
  headerAction?: React.ReactNode;
}

export const MultiLanguageInput: React.FC<MultiLanguageInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  multiline = false,
  rows,
  maxRows,
  error = false,
  helperText,
  disabled = false,
  requiredLanguages = ['no'],
  headerAction,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (languageCode: string, inputValue: string) => {
    onChange({
      ...value,
      [languageCode]: inputValue,
    });
  };

  const isLanguageRequired = (languageCode: string) => {
    return requiredLanguages.includes(languageCode);
  };

  const hasLanguageError = (languageCode: string) => {
    return error && isLanguageRequired(languageCode) && !value[languageCode]?.trim();
  };

  const getLanguageStatus = (language: LanguageTabConfig) => {
    const hasValue = !!value[language.code]?.trim();
    const isRequired = isLanguageRequired(language.code);
    const hasError = hasLanguageError(language.code);

    if (hasError) return 'error';
    if (hasValue) return 'completed';
    if (isRequired) return 'required';
    return 'optional';
  };

  const getTabColor = (status: string) => {
    switch (status) {
      case 'error':
        return 'error.main';
      case 'required':
        return 'warning.main';
      default:
        return 'text.secondary';
    }
  };

  const getStatusChip = (language: LanguageTabConfig) => {
    const status = getLanguageStatus(language);

    if (status === 'error') {
      return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    }
    if (status === 'completed') {
      return (
        <Chip
          label="✓"
          size="small"
          color="success"
          variant="outlined"
          sx={{ minWidth: 24, height: 20, fontSize: '0.65rem' }}
        />
      );
    }
    return null;
  };

  const currentLanguage = SUPPORTED_LANGUAGES[activeTab];

  return (
    <Box>
      {label && (
        <Typography
          variant="subtitle2"
          gutterBottom
          color={error ? 'error' : 'text.primary'}
          sx={{ fontWeight: 500 }}
        >
          {label}
          {required && ' *'}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {/* Language Tabs */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              flex: 1,
              minHeight: 48,
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontSize: '0.875rem',
              },
            }}
          >
            {SUPPORTED_LANGUAGES.map((language, _index) => {
              const status = getLanguageStatus(language);
              return (
                <Tab
                  key={language.code}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>{language.label}</span>
                      {getStatusChip(language)}
                    </Stack>
                  }
                  sx={{
                    color: getTabColor(status),
                    '&.Mui-selected': {
                      color: getTabColor(status),
                    },
                  }}
                />
              );
            })}
          </Tabs>
          {headerAction && <Box sx={{ px: 1.5, flexShrink: 0 }}>{headerAction}</Box>}
        </Box>

        {/* Input Field */}
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            value={value[currentLanguage.code] || ''}
            onChange={(e) => handleInputChange(currentLanguage.code, e.target.value)}
            placeholder={placeholder ? `${placeholder} (${currentLanguage.label})` : undefined}
            multiline={multiline}
            rows={rows}
            maxRows={maxRows}
            error={hasLanguageError(currentLanguage.code)}
            disabled={disabled}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
              },
            }}
          />

          {/* Helper Text */}
          {(helperText || hasLanguageError(currentLanguage.code)) && (
            <Typography
              variant="caption"
              color={hasLanguageError(currentLanguage.code) ? 'error' : 'text.secondary'}
              sx={{ mt: 0.5, display: 'block' }}
            >
              {hasLanguageError(currentLanguage.code)
                ? `${currentLanguage.label} translation is required`
                : helperText}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
